import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { socialService, SocialPost, SocialComment } from '../../services/socialService';
import { useAuth } from '../../contexts/AuthContext';
import { extractMentionedCharacterIds } from '../../utils/mentionUtils';
import PageHeader from '../common/PageHeader';
import PostCard from './PostCard';
import './PostDetail.css';

interface PostDetailProps {
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PostDetail: React.FC<PostDetailProps> = ({ onClose, onToggleSidebar }) => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState<SocialPost | null>(null);
  const [comments, setComments] = useState<SocialComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Comment states
  const [newCommentContent, setNewCommentContent] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set());
  const [replies, setReplies] = useState<Record<number, SocialComment[]>>({});
  const [loadingReplies, setLoadingReplies] = useState<Set<number>>(new Set());
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editPostContent, setEditPostContent] = useState('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_CHARS = 500;

  useEffect(() => {
    if (postId) {
      loadPostAndComments();
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [postId]);

  const loadPostAndComments = async () => {
    if (!postId) return;

    try {
      setLoading(true);
      setError(null);
      
      const [fetchedPost, fetchedComments] = await Promise.all([
        socialService.getPostById(parseInt(postId)),
        socialService.getComments(parseInt(postId))
      ]);
      
      setPost(fetchedPost);
      // Only show top-level comments (those without parent_comment_id)
      setComments(fetchedComments.filter(c => !c.parent_comment_id));
      
      // Check if post has mentions and start polling for AI responses
      const mentionedCharacterIds = extractMentionedCharacterIds(fetchedPost.content);
      if (mentionedCharacterIds.length > 0) {
        startPollingForAIComments(parseInt(postId!), fetchedComments.length);
      }
    } catch (error) {
      console.error('Failed to load post:', error);
      setError('Failed to load post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startPollingForAIComments = (postId: number, initialCommentCount: number) => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    let pollCount = 0;
    const maxPolls = 30; // Poll for up to 30 times (5 minutes if polling every 10 seconds)
    const pollInterval = 10000; // Poll every 10 seconds

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      
      try {
        const fetchedComments = await socialService.getComments(postId);
        const currentCommentCount = fetchedComments.length;
        
        // If we got new comments, reload the comments list
        if (currentCommentCount > initialCommentCount) {
          // Reload comments
          setComments(fetchedComments.filter(c => !c.parent_comment_id));
          
          // Update post comment count
          setPost(prevPost => prevPost ? { ...prevPost, comments_count: currentCommentCount } : prevPost);
          
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else if (pollCount >= maxPolls) {
          // Stop polling after max attempts
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error(`Error polling comments for post ${postId}:`, error);
        // Stop polling on error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }, pollInterval);
  };

  const handleBack = () => {
    navigate('/social');
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleLikeToggle = async () => {
    if (!post) return;

    try {
      if (post.is_liked) {
        await socialService.unlikePost(post.id);
        setPost({ ...post, is_liked: false, likes_count: (post.likes_count || 1) - 1 });
      } else {
        await socialService.likePost(post.id);
        setPost({ ...post, is_liked: true, likes_count: (post.likes_count || 0) + 1 });
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
      setError('Failed to update like. Please try again.');
    }
  };

  const handleStartEditPost = () => {
    if (!post) return;
    setEditingPostId(post.id);
    setEditPostContent(post.content);
  };

  const handleCancelEditPost = () => {
    setEditingPostId(null);
    setEditPostContent('');
  };

  const handleUpdatePost = async () => {
    if (!post || !editPostContent.trim()) return;

    try {
      const updatedPost = await socialService.updatePost(post.id, { 
        content: editPostContent.trim() 
      });
      setPost(updatedPost);
      setEditingPostId(null);
      setEditPostContent('');
    } catch (error) {
      console.error('Failed to update post:', error);
      setError('Failed to update post. Please try again.');
    }
  };

  const handleDeletePost = async () => {
    if (!post || !window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await socialService.deletePost(post.id);
      navigate('/social');
    } catch (error) {
      console.error('Failed to delete post:', error);
      setError('Failed to delete post. Please try again.');
    }
  };

  const handleCreateComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!post || !newCommentContent.trim()) return;

    try {
      const newComment = await socialService.createComment(post.id, { 
        content: newCommentContent.trim() 
      });
      setComments([...comments, newComment]);
      setNewCommentContent('');
      
      // Update comment count in post
      setPost({ ...post, comments_count: (post.comments_count || 0) + 1 });
    } catch (error) {
      console.error('Failed to create comment:', error);
      setError('Failed to create comment. Please try again.');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!post || !window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await socialService.deleteComment(post.id, commentId);
      setComments(comments.filter(c => c.id !== commentId));
      
      // Update comment count in post
      setPost({ ...post, comments_count: Math.max((post.comments_count || 1) - 1, 0) });
    } catch (error) {
      console.error('Failed to delete comment:', error);
      setError('Failed to delete comment. Please try again.');
    }
  };

  const handleStartEditComment = (comment: SocialComment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentContent('');
  };

  const handleUpdateComment = async (commentId: number) => {
    if (!post || !editCommentContent.trim()) return;

    try {
      const updatedComment = await socialService.updateComment(post.id, commentId, { 
        content: editCommentContent.trim() 
      });
      setComments(comments.map(c => c.id === commentId ? updatedComment : c));
      setEditingCommentId(null);
      setEditCommentContent('');
    } catch (error) {
      console.error('Failed to update comment:', error);
      setError('Failed to update comment. Please try again.');
    }
  };

  // Reply functions
  const handleStartReply = (commentId: number) => {
    setReplyingToCommentId(commentId);
    setReplyContent('');
  };

  const handleCancelReply = () => {
    setReplyingToCommentId(null);
    setReplyContent('');
  };

  const handleCreateReply = async (parentCommentId: number) => {
    if (!post || !replyContent.trim()) return;

    try {
      const newReply = await socialService.createComment(post.id, {
        content: replyContent.trim(),
        parent_comment_id: parentCommentId
      });
      
      // Add to replies state
      setReplies(prev => ({
        ...prev,
        [parentCommentId]: [...(prev[parentCommentId] || []), newReply]
      }));
      
      setReplyingToCommentId(null);
      setReplyContent('');
      
      // Update comment count in post
      setPost({ ...post, comments_count: (post.comments_count || 0) + 1 });
    } catch (error) {
      console.error('Failed to create reply:', error);
      setError('Failed to create reply. Please try again.');
    }
  };

  const toggleReplies = async (commentId: number) => {
    if (!post) return;

    const newExpandedReplies = new Set(expandedReplies);
    
    if (newExpandedReplies.has(commentId)) {
      newExpandedReplies.delete(commentId);
      setExpandedReplies(newExpandedReplies);
    } else {
      newExpandedReplies.add(commentId);
      setExpandedReplies(newExpandedReplies);
      
      // Load replies if not already loaded
      if (!replies[commentId]) {
        await loadReplies(commentId);
      }
    }
  };

  const loadReplies = async (commentId: number) => {
    if (!post) return;

    const newLoadingReplies = new Set(loadingReplies);
    newLoadingReplies.add(commentId);
    setLoadingReplies(newLoadingReplies);

    try {
      const fetchedReplies = await socialService.getReplies(post.id, commentId);
      setReplies(prev => ({ ...prev, [commentId]: fetchedReplies }));
    } catch (error) {
      console.error('Failed to load replies:', error);
      setError('Failed to load replies. Please try again.');
    } finally {
      const newLoadingRepliesAfter = new Set(loadingReplies);
      newLoadingRepliesAfter.delete(commentId);
      setLoadingReplies(newLoadingRepliesAfter);
    }
  };

  const handleDeleteReply = async (parentCommentId: number, replyId: number) => {
    if (!post || !window.confirm('Are you sure you want to delete this reply?')) return;

    try {
      await socialService.deleteComment(post.id, replyId);
      setReplies(prev => ({
        ...prev,
        [parentCommentId]: (prev[parentCommentId] || []).filter(r => r.id !== replyId)
      }));
      
      // Update comment count in post
      setPost({ ...post, comments_count: Math.max((post.comments_count || 1) - 1, 0) });
    } catch (error) {
      console.error('Failed to delete reply:', error);
      setError('Failed to delete reply. Please try again.');
    }
  };

  const handleUpdateReply = async (parentCommentId: number, replyId: number) => {
    if (!post || !editCommentContent.trim()) return;

    try {
      const updatedReply = await socialService.updateComment(post.id, replyId, { 
        content: editCommentContent.trim() 
      });
      setReplies(prev => ({
        ...prev,
        [parentCommentId]: (prev[parentCommentId] || []).map(r => r.id === replyId ? updatedReply : r)
      }));
      setEditingCommentId(null);
      setEditCommentContent('');
    } catch (error) {
      console.error('Failed to update reply:', error);
      setError('Failed to update reply. Please try again.');
    }
  };

  const renderComment = (comment: SocialComment, isReply: boolean = false) => {
    const isEditing = editingCommentId === comment.id;
    const commentReplies = replies[comment.id] || [];
    const hasReplies = comment.replies_count && comment.replies_count > 0;

    return (
      <div key={comment.id} className={`postdetail-comment ${isReply ? 'postdetail-comment-reply' : ''} ${comment.ai_character_id ? 'postdetail-comment-ai' : ''}`}>
        <div className="postdetail-comment-header">
          <div className="postdetail-comment-author">
            {comment.profile_picture_url ? (
              <img 
                src={comment.profile_picture_url} 
                alt={comment.display_name || comment.username}
                className="postdetail-comment-avatar"
              />
            ) : (
              <div className={`postdetail-comment-avatar-placeholder ${comment.ai_character_id ? 'postdetail-comment-ai-avatar' : ''}`}>
                {comment.ai_character_name 
                  ? comment.ai_character_name[0].toUpperCase()
                  : (comment.display_name || comment.username || 'U')[0].toUpperCase()}
              </div>
            )}
            <div className="postdetail-comment-author-info">
              <span className="postdetail-comment-author-name">
                {comment.ai_character_name 
                  ? `${comment.ai_character_name} (AI)`
                  : (comment.display_name || comment.username || 'Anonymous')}
              </span>
              <span className="postdetail-comment-time">{formatTimeAgo(comment.created_at)}</span>
            </div>
          </div>
          {user?.id === comment.user_id && (
            <div className="postdetail-comment-menu">
              {isEditing ? (
                <>
                  <button 
                    className="postdetail-comment-menu-btn postdetail-save-btn"
                    onClick={() => isReply 
                      ? handleUpdateReply(comment.parent_comment_id!, comment.id)
                      : handleUpdateComment(comment.id)
                    }
                  >
                    Save
                  </button>
                  <button 
                    className="postdetail-comment-menu-btn postdetail-cancel-btn"
                    onClick={handleCancelEditComment}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="postdetail-comment-menu-btn"
                    onClick={() => handleStartEditComment(comment)}
                  >
                    Edit
                  </button>
                  <button 
                    className="postdetail-comment-menu-btn postdetail-delete-btn"
                    onClick={() => isReply 
                      ? handleDeleteReply(comment.parent_comment_id!, comment.id)
                      : handleDeleteComment(comment.id)
                    }
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="postdetail-comment-content">
          {isEditing ? (
            <textarea
              value={editCommentContent}
              onChange={(e) => setEditCommentContent(e.target.value)}
              className="postdetail-comment-edit-input"
              maxLength={MAX_CHARS}
              rows={2}
            />
          ) : (
            <p>{comment.content}</p>
          )}
        </div>

        {!isReply && (
          <div className="postdetail-comment-actions">
            <button
              className="postdetail-reply-btn"
              onClick={() => handleStartReply(comment.id)}
            >
              Reply
            </button>
            {hasReplies && (
              <button
                className="postdetail-view-replies-btn"
                onClick={() => toggleReplies(comment.id)}
              >
                {expandedReplies.has(comment.id) ? '▼' : '▶'} {comment.replies_count} {comment.replies_count === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>
        )}

        {/* Reply input */}
        {replyingToCommentId === comment.id && (
          <div className="postdetail-reply-input-container">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              className="postdetail-reply-input"
              maxLength={MAX_CHARS}
              rows={2}
            />
            <div className="postdetail-reply-actions">
              <button
                onClick={() => handleCreateReply(comment.id)}
                disabled={!replyContent.trim()}
                className="postdetail-reply-submit-btn"
              >
                Reply
              </button>
              <button
                onClick={handleCancelReply}
                className="postdetail-reply-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Replies list */}
        {expandedReplies.has(comment.id) && (
          <div className="postdetail-replies-list">
            {loadingReplies.has(comment.id) ? (
              <div className="postdetail-replies-loading">Loading replies...</div>
            ) : (
              <>
                {commentReplies.map(reply => renderComment(reply, true))}
                {commentReplies.length === 0 && (
                  <div className="postdetail-no-replies">No replies yet.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="postdetail-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={
            <h1 className="postdetail-title">Post</h1>
          }
        />
        <div className="postdetail-loading-container">
          <div className="postdetail-loading-spinner"></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="postdetail-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={
            <h1 className="postdetail-title">Post</h1>
          }
          rightActions={
            <button className="postdetail-close-btn" onClick={onClose}>×</button>
          }
        />
        <div className="postdetail-error-container">
          <p>{error || 'Post not found'}</p>
          <button onClick={handleBack} className="postdetail-back-btn">
            Back to Social
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="postdetail-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="postdetail-title">Post</h1>
        }
        rightActions={
          <button className="postdetail-close-btn" onClick={onClose}>×</button>
        }
      />

      <div className="postdetail-container">
        {/* Error Message */}
        {error && (
          <div className="postdetail-error-message">
            <span>{error}</span>
            <button 
              className="postdetail-error-dismiss"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        <div className="postdetail-content">
          {/* Post Card */}
          <PostCard
            post={post}
            currentUserId={user?.id}
            isEditing={editingPostId === post.id}
            editContent={editPostContent}
            onEditContentChange={setEditPostContent}
            onStartEdit={handleStartEditPost}
            onSaveEdit={handleUpdatePost}
            onCancelEdit={handleCancelEditPost}
            onDelete={handleDeletePost}
            onLikeToggle={handleLikeToggle}
            maxChars={MAX_CHARS}
            showBackButton={true}
            onBackClick={handleBack}
          />

          {/* Comments Section */}
          <div className="postdetail-comments-section">
            <h2 className="postdetail-comments-title">Comments</h2>

            {/* Create Comment Form */}
            <form onSubmit={handleCreateComment} className="postdetail-comment-form">
              <textarea
                value={newCommentContent}
                onChange={(e) => setNewCommentContent(e.target.value)}
                placeholder="Write a comment..."
                className="postdetail-comment-input"
                maxLength={MAX_CHARS}
                rows={3}
              />
              <div className="postdetail-comment-form-actions">
                <span className="postdetail-char-count">
                  {newCommentContent.length}/{MAX_CHARS}
                </span>
                <button
                  type="submit"
                  disabled={!newCommentContent.trim()}
                  className="postdetail-comment-submit-btn"
                >
                  Comment
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="postdetail-comments-list">
              {comments.length === 0 ? (
                <div className="postdetail-no-comments">
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                comments.map(comment => renderComment(comment))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;

